import { DefaultOptionType } from 'antd/lib/select';
import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import { useHistory, useLocation } from 'react-router-dom';
import { ROUTES } from 'shared/constants/ROUTES';
import { useFilter } from 'shared/hooks/useFilter';
import { usePagination } from 'shared/hooks/usePagination';
import { useSearch } from 'shared/hooks/useSearch';
import { useSelectOptions } from 'shared/hooks/useSelectOptions';
import { IChannel } from 'shared/interfaces/IChannel';
import { IProgram } from 'shared/interfaces/IProgram';
import { IProgramSetup } from 'shared/interfaces/IProgramSetup';
import { useChannelService } from 'shared/services/channelService';
import { useProgramService } from 'shared/services/programService';
import { useProgramSetupService } from 'shared/services/programSetupService';
import { Alert } from 'shared/theme/elements';

import ListView from './ListView';
import { useAVAutomationService } from 'shared/services/avAutomationService';
import { DeleteConfirmModal } from '../ProgramSetup/components/DeleteConfirmModal';

export interface ProgramSetupFilter {
  channel?: string;
  program?: string;
}

export interface ProgramSetupSearch {
  program?: string;
  channel?: string;
}

const filterDefaultValues: ProgramSetupFilter = {
  channel: undefined,
  program: undefined,
};

const searchDefaultValues: ProgramSetupSearch = {
  program: undefined,
  channel: undefined,
};

export interface ProgramSetupSelectOptions {
  channel: DefaultOptionType[];
  program: DefaultOptionType[];
}

export interface ProgramSetupLoading {
  channel?: boolean;
  program?: boolean;
  programSetup?: boolean;
}

const ListContainer = () => {
  const queryClient = useQueryClient();
  const history = useHistory();
  const { state } = useLocation();
  const locationState = state as any;

  const pagination = usePagination({ key: 'program-setup' });
  const { page, pageSize, handlePageChange } = pagination;

  const { filter, setFilter } = useFilter<ProgramSetupFilter>(filterDefaultValues);
  const { search, setSearch } = useSearch<ProgramSetupSearch>(searchDefaultValues, {
    options: {
      channel: {
        delay: 800,
      },
      program: {
        delay: 800,
      },
    },
  });

  // Set default channel filter to GMA 7 on mount
  useEffect(() => {
    if (!filter.channel) {
      setFilter('channel', '619f1734a834a290ce566c1b');
    }
  }, [filter.channel, setFilter]);

  /**
   * Channel Service
   */
  const { getChannel } = useChannelService();
  const { data: channel, isLoading: channelLoading } = getChannel({
    media_source_type_flag: ['video'],
    channel_name: search.channel,
    limit: 20,
    country: ['PHILIPPINES'],
  });
  const channelData = useMemo(() => channel?.data, [channel?.data]);
  const { data: channelOptions } = useSelectOptions<IChannel>({
    data: channelData,
    labelKey: 'publisher_name',
    valueKey: 'publisher_obj_id',
  });

  /**
   * Program Service
   */
  const { getProgram } = useProgramService();
  const { data: program, isLoading: programLoading } = getProgram({
    channel_id: filter.channel,
    program_name: search.program,
  });
  const programData = useMemo(() => program?.data, [program?.data]);
  const { data: programOptions } = useSelectOptions<IProgram>({
    data: programData,
    labelKey: 'program_name',
    valueKey: '_id',
  });

  // Every program on the channel, for the Program Type column (the type lives on the program).
  const { data: channelPrograms } = getProgram({
    channel_id: filter.channel,
    limit: 500,
    media_source_type_flag: 'video',
  });
  const programById = useMemo(() => {
    const byId: Record<string, IProgram> = {};
    (Array.isArray(channelPrograms?.data) ? channelPrograms?.data ?? [] : []).forEach((record) => {
      byId[record._id] = record;
    });
    return byId;
  }, [channelPrograms?.data]);

  /**
   * Audio Video Automation - Program Setup Service
   */
  const { getProgramSetup, deleteProgramSetup } = useProgramSetupService();
  const { data: programSetup, isLoading: setupLoading } = getProgramSetup({
    page: page,
    limit: pageSize,
    sort: '',
    channel_obj_id: filter.channel,
    program_obj_id: filter.program,
    machine_type: locationState?.machine_type,
    machine_ip: locationState?.machine_ip,
    audio_setup: null,
  });
  // const programSetupData = useMemo(() => programSetup?.data?.[0], [programSetup?.data]);

  // Any one program on the channel carries the channel-level recording config.
  const { data: channelProgram, isLoading: channelProgramLoading } = getProgramSetup(
    filter.channel
      ? {
          page: 1,
          limit: 1,
          sort: '',
          channel_obj_id: filter.channel,
          audio_setup: null,
        }
      : undefined,
  );
  const channelRepresentative = channelProgram?.data?.[0];

  const programSetupOptions: ProgramSetupSelectOptions = useMemo(
    () => ({
      channel: channelOptions,
      program: programOptions,
    }),
    [channelOptions, programOptions],
  );

  const programSetupLoading: ProgramSetupLoading = useMemo(
    () => ({
      channel: channelLoading,
      program: programLoading,
      programSetup: setupLoading,
    }),
    [channelLoading, programLoading, setupLoading],
  );

  /**
   * Delete Program Setup
   * @param id program setup id
   * @returns axios promise
   */
  const deleteSetup = (id: string) => {
    const { deleteMutation } = deleteProgramSetup();

    return new Promise((resolve, reject) => {
      deleteMutation.mutateAsync(id, {
        onSuccess: () => {
          resolve(true);
          Alert.success('Program Setup Deleted!');
          queryClient.invalidateQueries('program_setup');
          queryClient.invalidateQueries('av_gaugemeter');
        },
        onError: (error: any) => {
          reject();
          Alert.error(error.response.data.server_response);
        },
      });
    });
  };

  /**
   * Handle on program setup list edit button
   * @param id Program Setup ID
   */
  const handleOnEdit = (id: string) => {
    history.push(ROUTES.TV.PROGRAMSETUP.EDIT, {
      id,
    });
  };

  /**
   * When delete button is clicked this opens the confirmation modal before actual delete
   * @param id Program Setup ID
   * @param channel Selected Program Setup Channel (for display only)
   * @param program Selecte Program Setup Program (for display only)
   */
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; channel: string; program: string } | null>(null);
  const [deleting, setDeleting] = useState(false);

  const handleOnDelete = (
    id: string,
    channel: Pick<IProgramSetup, 'channel_details'>['channel_details'],
    program: Pick<IProgramSetup, 'program_details'>['program_details'],
  ) => {
    setDeleteTarget({ id, channel: channel.channel_name, program: program.program_name });
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteSetup(deleteTarget.id);
    } catch {
      // Alert.error already surfaced the failure inside deleteSetup's onError.
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  const handleOnFilter = (key: keyof ProgramSetupFilter, value?: any) => {
    setFilter(key, value);
    handlePageChange(1);
  };

  useEffect(() => {
    queryClient.invalidateQueries('program_setup');
    queryClient.invalidateQueries('av_gaugemeter');
  }, [queryClient]);

  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [selectedIP, setSelectedIP] = useState<string>('');

  const newSelectedRows: string[] = programSetup?.data?.map((r) => r?._id) || [];

  const handleSelect = (record: any, selected: string[]) => {
    if (selected) {
      setSelectedRowKeys((keys) => [...keys, record.key]);
    } else {
      setSelectedRowKeys((keys) => {
        const index = keys.indexOf(record.key);
        return [...keys.slice(0, index), ...keys.slice(index + 1)];
      });
    }
  };

  const toggleSelectAll = (rowsChecked: any) => {
    if (rowsChecked.target.checked) {
      setSelectedRowKeys((keys) => [...keys.filter((key) => !newSelectedRows.includes(key)), ...newSelectedRows]);
    } else {
      setSelectedRowKeys((keys) => keys.filter((key) => !newSelectedRows.includes(key)));
    }
  };

  const handleChangeIP = (value) => {
    setSelectedIP(value);
  };

  const { getGaugeMeter, transferMachine } = useAVAutomationService();
  const { transferMachineMutation } = transferMachine();

  const transferMachineIP = () => {
    return new Promise((resolve, reject) => {
      transferMachineMutation.mutateAsync(
        {
          av_setup_ids: selectedRowKeys,
          transfer_machine_ip: selectedIP,
          transfer_machine_type: locationState?.machine_type,
        },
        {
          onSuccess: () => {
            resolve(true);
            Alert.success('Programs Transfered');
            queryClient.invalidateQueries('program_setup');
            queryClient.invalidateQueries('av_gaugemeter');
            setSelectedIP('');
            setSelectedRowKeys([]);
          },
          onError: (error: any) => {
            reject();
            Alert.error(error.response.data.server_response);
          },
        },
      );
    });
  };

  const { data: gaugeData } = getGaugeMeter({
    machine_type: locationState?.machine_type ? [locationState?.machine_type] : undefined,
    limit: 100,
  });

  const IPOption = gaugeData?.data
    ?.filter((value) => (locationState?.machine_ip ? locationState?.machine_ip !== value.machine_ip : value))
    .map(({ machine_ip }) => ({ value: machine_ip, label: machine_ip }));

  useEffect(() => {
    if (selectedRowKeys.length === 0) {
      setSelectedIP('');
    }
  }, [selectedRowKeys]);

  return (
    <>
      <ListView
        data={programSetup}
        pagination={pagination}
        onDelete={handleOnDelete}
        onEdit={handleOnEdit}
        onFilter={handleOnFilter}
        filters={filter}
        search={search}
        onSearch={setSearch}
        options={programSetupOptions}
        loading={programSetupLoading}
        handleSelect={handleSelect}
        toggleSelectAll={toggleSelectAll}
        selectedRowKeys={selectedRowKeys}
        newSelectedRows={newSelectedRows}
        locationState={locationState}
        IPOption={IPOption}
        selectedIP={selectedIP}
        handleChangeIP={handleChangeIP}
        transferMachineIP={transferMachineIP}
        channelRepresentative={channelRepresentative}
        channelSettingsLoading={channelProgramLoading}
        programById={programById}
      ></ListView>
      <DeleteConfirmModal
        open={!!deleteTarget}
        bodyText={
          deleteTarget
            ? `"${deleteTarget.program}" will be removed from ${deleteTarget.channel}. This can't be undone.`
            : ''
        }
        loading={deleting}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
};

export default ListContainer;
